import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, CalendarRange, Layers, Plus, UserPlus, UserRound, UsersRound } from 'lucide-react'
import { Link } from 'react-router'
import { ErrorState } from '@/components/feedback/States'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { StatCard } from '@/components/ui/StatCard'
import { Can } from '@/features/auth/guards'
import { useAuth } from '@/features/auth/useAuth'
import { dashboardQuery, type DashboardData } from '@/features/dashboard/api'
import { AdmissionsChart } from '@/features/dashboard/components/AdmissionsChart'
import { AttendanceTodayCard } from '@/features/dashboard/components/AttendanceTodayCard'
import { LatestNotices } from '@/features/dashboard/components/LatestNotices'
import { FinanceCard, LibraryCard, UpcomingExamsCard } from '@/features/dashboard/components/ModuleCards'
import { ClassBarChart } from '@/features/dashboard/components/ClassBarChart'
import { RankedBars } from '@/features/dashboard/components/RankedBars'
import { SplitBar } from '@/features/dashboard/components/SplitBar'
import { SetupGuideCard } from '@/features/setup/SetupGuideCard'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatDate, formatNumber, formatPercent } from '@/utils/format'

function useGreeting(name: string): string {
  const { t } = useTranslation('dashboard')
  const h = new Date().getHours()
  return h < 12
    ? t('Good morning, {{name}}', { name })
    : h < 18
      ? t('Good afternoon, {{name}}', { name })
      : t('Good evening, {{name}}', { name })
}

function YearProgress({ year }: { year: NonNullable<DashboardData['academicYear']> }) {
  const { t } = useTranslation('dashboard')
  const [now] = useState(() => Date.now())
  const start = new Date(`${year.startsOn}T00:00:00`).getTime()
  const end = new Date(`${year.endsOn}T00:00:00`).getTime()
  const pct = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100))
  const daysLeft = Math.max(0, Math.ceil((end - now) / 86_400_000))
  return (
    <div className="bg-brand-gradient relative overflow-hidden rounded-card p-5 text-white shadow-brand">
      <div aria-hidden="true" className="absolute -top-12 -right-12 size-40 rounded-full bg-white/10" />
      <p className="flex items-center gap-2 text-xs font-medium tracking-wide text-white/75 uppercase">
        <CalendarRange className="size-4" aria-hidden="true" /> {t('Current academic year')}
      </p>
      <p className="mt-2 text-2xl font-semibold">{year.name}</p>
      <p className="mt-1 text-sm text-white/75">
        {formatDate(year.startsOn)} – {formatDate(year.endsOn)}
      </p>
      <div className="mt-5">
        <div className="flex justify-between text-xs text-white/80">
          <span>{t('{{pct}}% complete', { pct: Math.round(pct) })}</span>
          <span>{t('{{value}} days left', { count: daysLeft, value: formatNumber(daysLeft) })}</span>
        </div>
        <div
          className="mt-1.5 h-2 overflow-hidden rounded-full bg-white/20"
          role="progressbar"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('Academic year progress')}
        >
          <div className="h-full rounded-full bg-white" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  )
}

export function DashboardPage() {
  const { t } = useTranslation('dashboard')
  const { user, can, school } = useAuth()
  const title = useGreeting(user?.name.split(' ')[0] ?? '')
  const { data, isPending, error, refetch } = useQuery(dashboardQuery())
  useDocumentTitle('Dashboard')

  if (error) return <ErrorState onRetry={() => void refetch()} />

  const k = data?.kpis

  return (
    <>
      <PageHeader
        title={title}
        description={t("Here's what's happening across your school today.")}
        actions={
          <>
            <Can permission="staff.manage">
              <Button asChild variant="secondary" leftIcon={<UserPlus />}>
                <Link to="/admin/staff/new">{t('Hire staff')}</Link>
              </Button>
            </Can>
            <Can permission="students.manage">
              <Button asChild leftIcon={<Plus />}>
                <Link to="/admin/students/new">{t('Admit student')}</Link>
              </Button>
            </Can>
          </>
        }
      />

      <SetupGuideCard />

      <section aria-label={t('Key figures')} className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('Active students')}
          value={formatNumber(k?.activeStudents)}
          icon={<UsersRound />}
          loading={isPending}
          hint={
            k
              ? t('{{value}} inactive or former', {
                  count: k.inactiveStudents,
                  value: formatNumber(k.inactiveStudents),
                })
              : undefined
          }
        />
        <StatCard
          label={t('Active staff')}
          value={formatNumber(k?.activeStaff)}
          icon={<UserRound />}
          tone="violet"
          loading={isPending}
          hint={t('Teaching and support staff')}
        />
        <StatCard
          label={t('Classes')}
          value={formatNumber(k?.classes)}
          icon={<Layers />}
          tone="info"
          loading={isPending}
          hint={
            k
              ? t('{{sections}} sections · {{seats}} seats', {
                  sections: formatNumber(k.sections),
                  seats: formatNumber(k.seatCapacity),
                })
              : undefined
          }
        />
        <StatCard
          label={t('Seat occupancy')}
          value={k?.occupancy !== null && k?.occupancy !== undefined ? formatPercent(k.occupancy) : '—'}
          icon={<CalendarRange />}
          tone="success"
          loading={isPending}
          hint={
            k ? (
              <span className="block">
                <span
                  className="mb-1.5 block h-1.5 overflow-hidden rounded-full bg-surface-sunken"
                  aria-hidden="true"
                >
                  <span
                    className="block h-full rounded-full bg-success-500"
                    style={{ width: `${Math.min(100, k.occupancy ?? 0)}%` }}
                  />
                </span>
                {t('of section capacity filled')}
              </span>
            ) : undefined
          }
        />
      </section>

      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader
            title={t('New admissions')}
            description={t('Students admitted per month, last 12 months')}
          />
          <CardBody>
            {!data ? <Skeleton className="h-64 w-full" /> : <AdmissionsChart data={data.admissionsByMonth} />}
          </CardBody>
        </Card>
        <div className="flex flex-col gap-6">
          {data?.academicYear ? (
            <YearProgress year={data.academicYear} />
          ) : isPending ? (
            <Skeleton className="h-44 w-full rounded-card" />
          ) : null}
          <Card className="flex-1">
            <CardHeader title={t('Students by gender')} description={t('Active students')} />
            <CardBody>
              {!data ? <Skeleton className="h-24 w-full" /> : <SplitBar data={data.studentsByGender} />}
            </CardBody>
          </Card>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader
            title={t('Enrollment by class')}
            description={t('Active students in the current academic year')}
            actions={
              <Button asChild variant="ghost" size="sm" rightIcon={<ArrowRight />}>
                <Link to="/admin/academic/classes">{t('Classes')}</Link>
              </Button>
            }
          />
          <CardBody>
            {!data ? <Skeleton className="h-64 w-full" /> : <ClassBarChart data={data.studentsByClass} />}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title={t('Staff by department')} description={t('Active staff')} />
          <CardBody>
            {!data ? (
              <div className="space-y-4">
                {Array.from({ length: 5 }, (_, i) => (
                  <Skeleton key={i} className="h-7 w-full" />
                ))}
              </div>
            ) : (
              <RankedBars data={data.staffByDepartment} />
            )}
          </CardBody>
        </Card>
      </div>

      {data ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
          {data.finance ? <FinanceCard data={data.finance} currency={school?.currency ?? 'USD'} /> : null}
          {data.attendanceToday ? <AttendanceTodayCard data={data.attendanceToday} /> : null}
          {data.upcomingExams ? <UpcomingExamsCard exams={data.upcomingExams} /> : null}
          {data.library ? <LibraryCard data={data.library} currency={school?.currency ?? 'USD'} /> : null}
          {can('announcements.view') ? (
            <LatestNotices
              items={data.announcements}
              href={can('announcements.manage') ? '/admin/announcements' : '/admin/notices'}
            />
          ) : null}
        </div>
      ) : (
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-56 w-full rounded-card" />
          ))}
        </div>
      )}

      <Card className="mt-6">
        <CardHeader
          title={t('Recently admitted')}
          description={t('The latest students added to the register')}
          actions={
            <Button asChild variant="ghost" size="sm" rightIcon={<ArrowRight />}>
              <Link to="/admin/students?sort=createdAt&direction=desc">{t('All students')}</Link>
            </Button>
          }
        />
        <ul className="grid divide-y divide-line sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-3">
          {(data?.recentStudents ?? []).map((s) => (
            <li key={s.id}>
              <Link
                to={`/admin/students/${s.id}`}
                className="flex items-center gap-3 px-5 py-4 transition-colors hover:bg-surface-muted"
              >
                <Avatar name={s.fullName} src={s.photoUrl} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink">{s.fullName}</span>
                  <span className="block truncate text-xs text-muted">
                    {s.className ?? t('Not enrolled')} · {s.studentCode}
                  </span>
                </span>
                <span className="text-xs text-subtle">{formatDate(s.admissionDate)}</span>
              </Link>
            </li>
          ))}
        </ul>
        {isPending ? (
          <div className="p-5">
            <Skeleton className="h-10 w-full" />
          </div>
        ) : null}
      </Card>
    </>
  )
}
